// Import Redux/toolkit
import { createAsyncThunk } from "@reduxjs/toolkit";

// Import AuthConfig
import authConfig from "../../configs/auth";

// Import API endpoints
import { ApiEndPoints } from "src/network/endpoints";

// Import AxiosInstance
import { axiosInstance } from "src/network/adapter";
import toast from "react-hot-toast";

const login = createAsyncThunk(
  "auth/login",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post(
        ApiEndPoints.AUTH.login,
        payload
      );

      const { accessToken } = response?.data;
      const user = response?.data?.user;

      // If user is deleted, handle it differently (no token needed)
      if (user?.isDeleted === 1 && user?._id) {
        return { user: user, isDeleted: 1 };
      }

      if (!accessToken || !user?._id) {
        return rejectWithValue("Missing token or userId");
      }

      // ✅ Save token, userData, and userId
      window.localStorage.setItem(authConfig.storageTokenKeyName, accessToken);
      window.localStorage.setItem(authConfig.storageUserDataKeyName, JSON.stringify(user));
      window.localStorage.setItem(authConfig.storageUserIdKeyName, user._id);

      const meResponse = await axiosInstance.get(ApiEndPoints.AUTH.userInfo(user._id), {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          userid: user._id, 
          "Content-Type": "application/json",
          env: "test",
        },
      });

      const updatedUser = meResponse?.data?.data?.data;
      window.localStorage.setItem(
        authConfig.storageUserDataKeyName,
        JSON.stringify(updatedUser)
      );
      
      // Step 2: Call fetchUser to get complete profile and check completion status
      // This ensures the profile completion dialog opens immediately after login
      try {
        const fetchUserResult = await dispatch(fetchUser({ 
          storedToken: accessToken,
          userId: user._id 
        }));
        
        if (fetchUserResult.type === "auth/fetchUser/fulfilled") {
          // fetchUser successful, profile completion status is now available
        }
      } catch (fetchUserError) {
        // If fetchUser fails, log the error but don't fail the login
        console.error("Login: fetchUser after login failed:", fetchUserError);
      }
      
      toast.success('You are now logged in!');
      
      return { user: updatedUser, isDeleted: user?.isDeleted };
    } catch (error) {
      toast.error(error.response?.data?.error || "Login failed");
      return rejectWithValue(error.response?.data?.message || "Login failed");
    }
  }
);

const fetchUser = createAsyncThunk(
  "auth/fetchUser",
  async ({ storedToken }, { rejectWithValue }) => {
    if (!storedToken) {
      return rejectWithValue("No token found");
    }

    try {
      // ✅ Get userId from localStorage
      const userId = window.localStorage.getItem(authConfig.storageUserIdKeyName);
      if (!userId) {
        return rejectWithValue("UserId not found");
      }

      const response = await axiosInstance.get(ApiEndPoints.AUTH.userInfo(userId), {
        headers: {
          Authorization: `Bearer ${storedToken}`,
          userid: userId, // ✅ Pass userId here
          "Content-Type": "application/json",
          env: "test",
        },
      });

      return response?.data?.data?.data;
    } catch (error) {
      // Don't clear user data if we're on payment routes or in a popup window
      const currentPath = window.location?.pathname || '';
      const isPaymentRoute = currentPath.includes('/subscription/cancel') || 
                            currentPath.includes('/subscription/success');
      const isPopupWindow = window.opener && !window.opener.closed;
      
      // Only clear user data if it's a legitimate authentication failure
      // and we're not on a payment page or in a popup
      if (!isPaymentRoute && !isPopupWindow && error.response?.status === 401) {
        window.localStorage.removeItem(authConfig.storageTokenKeyName);
        window.localStorage.removeItem(authConfig.storageUserDataKeyName);
        window.localStorage.removeItem(authConfig.storageUserIdKeyName);
      }

      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch user"
      );
    }
  }
);

const register = createAsyncThunk(
  "auth/register",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      // Step 1: Registration API call
      // This creates the user account in the backend
      const response = await axiosInstance.post(
        ApiEndPoints.AUTH.register,
        payload
      );

      // Step 2: After successful registration, automatically log in the user
      // This provides a seamless user experience - no need to manually login after registration
      try {
        // Construct login payload using the same credentials from registration
        // This ensures the user is authenticated with the same email/password they just registered
        const loginPayload = {
          userName: {
            2: payload.userName[2], // Email from registration (type 2 for email)
          },
          password: payload.password, // Password from registration
          device: 3,                  // Device type (common value)
        };

        // Call the login action to authenticate the newly registered user
        // This will create a session and return user data if successful
        const loginResult = await dispatch(login(loginPayload));
        
        if (loginResult.type === "auth/login/fulfilled") {
          // Auto-login successful - user is now registered AND logged in
          
          // Step 3: Call fetchUser to get complete user profile and check completion status
          // This is important because the login action only returns basic user data
          // fetchUser will return the full profile including isProfileComplete flag
          try {
            // Get access token from login response or fallback to localStorage
            const accessToken = loginResult.payload.accessToken || 
                              window.localStorage.getItem(authConfig.storageTokenKeyName);
            
            // Get userId from login response or localStorage
            const userId = loginResult.payload.user?._id || 
                          window.localStorage.getItem(authConfig.storageUserIdKeyName);
            
            if (accessToken && userId) {
              // Call fetchUser to get complete profile and trigger profile completion check
              const fetchUserResult = await dispatch(fetchUser({ 
                storedToken: accessToken,
                userId: userId 
              }));
              
              if (fetchUserResult.type === "auth/fetchUser/fulfilled") {
                // fetchUser successful, profile completion status is now available
              }
            }
          } catch (fetchUserError) {
            // If fetchUser fails, log the error but don't fail the registration
            console.error("Registration: fetchUser after registration failed:", fetchUserError);
          }
          
          // Return success status with user data for immediate access
          toast.success('Registration has been completed! Please login to continue.');
          return { 
            ...response.data,           // Original registration response
            autoLoginSuccess: true,     // Flag indicating auto-login worked
            user: loginResult.payload.user // User data from successful login
          };
        } else {
          // Auto-login failed after successful registration
          // This could happen due to API issues, server problems, or validation errors
          // User will need to manually login with their credentials
          toast.success('Registration has been completed! Please login to continue.');
          return { 
            ...response.data,           // Original registration response
            autoLoginSuccess: false     // Flag indicating auto-login failed
          };
        }
      } catch (loginError) {
        // Auto-login failed due to an exception
        // Log the error for debugging but don't fail the entire registration
        console.error("Auto-login after registration failed:", loginError);
        toast.success('Registration has been completed! Please login to continue.');
        return { 
          ...response.data,           // Original registration response
          autoLoginSuccess: false     // Flag indicating auto-login failed
        };
      }
    } catch (error) {
      // Registration completely failed
      // Show error message and return error details
      toast.error(error.response?.data?.message || "Registration failed");
      return rejectWithValue(error.response?.data?.message || "Registration failed");
    }
  }
);

// ** NEW: Forgot Password Action **
const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        ApiEndPoints.AUTH.forgotPassword,
        {
          userName: {
            2: payload.email,
          },
          device: 3,
        }
      );
      
      toast.success('OTP sent  !');
      return { 
        email: payload.email, // Store email for later use
        message: response.data.message 
      };
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to send OTP");
      return rejectWithValue(error.response?.data?.message || "Failed to send OTP");
    }
  }
);

// ** NEW: Verify OTP Action **
const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async (payload, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const email = auth.forgotPasswordEmail;
      
      const response = await axiosInstance.post(
        ApiEndPoints.AUTH.verifyOtp,
        {
          otp: payload.otp,
          userName: {
            2: email
          },
          device: 3
        }
      );
      
      toast.success('OTP verified  !');
      // Return the token along with other response data
      return {
        ...response.data,
        token: response.data.token || response.data.accessToken // Handle different possible token field names
      };
    } catch (error) {
      toast.error(error.response?.data?.message || "Invalid OTP. Please try again.");
      return rejectWithValue(error.response?.data?.message || "Invalid OTP. Please try again.");
    }
  }
);

// ** NEW: Resend OTP Action **
const resendOtp = createAsyncThunk(
  "auth/resendOtp",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const email = auth.forgotPasswordEmail;
      
      const response = await axiosInstance.post(
        ApiEndPoints.AUTH.resendOtp,
        {
          userName: {
            2: email
          },
          device: 3,
          reason: "forForgotPassword"
        }
      );
      
      toast.success('OTP sent  !');
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to resend OTP");
      return rejectWithValue(error.response?.data?.message || "Failed to resend OTP");
    }
  }
);

const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (payload, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const resetToken = auth.resetPasswordToken;
      
      // Check if we have the required token
      if (!resetToken) {
        return rejectWithValue("Reset password token not found. Please verify OTP first.");
      }
      
      const response = await axiosInstance.post(
        ApiEndPoints.AUTH.resetPassword,
        {
          confirmPassword: payload.password,
          password:payload.password,
          device: 3,
          token :resetToken
        }
      );
      
      toast.success('Password reset  !');
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to reset password");
      return rejectWithValue(error.response?.data?.message || "Failed to reset password");
    }
  }
);

export { login, fetchUser, register, forgotPassword, verifyOtp, resendOtp, resetPassword };